/**
 * KAN-468 — Learner profile self-edit: pure functions.
 *
 * Business logic for the learner-facing profile fields section. Components
 * orchestrate; everything here is a pure function (docs/patterns/
 * angular-fp-patterns.md).
 *
 * Built against the KAN-468 backend API contract v1 (2026-08-25):
 * fields are org custom-field definitions (KAN-136 infra) carrying
 * `isRequested`, delivered as FieldWithValue objects from
 * GET /custom-fields/me. Birth month+year is a `text` field
 * (fieldKey `birth_month_year`) whose value is an `MM/YYYY` string —
 * deliberately month+year only, never a day (PII minimization). The UI
 * renders it as a month+year picker; these helpers translate picker parts
 * to/from the contract's MM/YYYY wire format.
 */
import { formatCustomFieldValue } from '../../shared/utils/custom-field-display.utils';

/**
 * KAN-562 follow-up: the word the LEARNER sees on a field their org has asked
 * for but they have not filled in.
 *
 * It reads "Required" because that is what it means to them - the course gate
 * (profileCompletionGuard on courses/:id) will not let them open a course
 * until every one of these is saved. Harold's locked-out user read the old
 * "Requested" as optional.
 *
 * SCOPE: this is a display label only. It is NOT the `isRequested` column and
 * NOT the separate `isRequired` column - those remain two distinct flags with
 * distinct meanings, and the instructor-facing custom-field modal still names
 * them "Requested" and "Required" separately.
 *
 * Shared because the profile gate banner tells the learner to fill in "every
 * item marked <this>"; if the two ever disagree the instruction points at a
 * label that is not on screen.
 */
export const REQUESTED_FIELD_BADGE_LABEL = 'Required';

export type ProfileFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'dropdown'
  | 'boolean'
  | 'email'
  | 'phone';

export interface ProfileFieldOptions {
  options?: Array<{ value: string; label: string }>;
  allowOther?: boolean;
}

export interface ProfileFieldValidationRules {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  dateMin?: string;
  dateMax?: string;
}

/** Contract v1 FieldWithValue (payload inside the {success,data} envelope). */
export interface ProfileFieldWithValue {
  fieldId: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: ProfileFieldType;
  fieldOptions?: ProfileFieldOptions | null;
  isRequired?: boolean;
  /** Org asks learners to fill this in; powers the completion prompt. */
  isRequested: boolean;
  displayOrder?: number;
  description?: string | null;
  validationRules?: ProfileFieldValidationRules | null;
  value: unknown;
  valueUpdatedAt?: string | null;
}

/** Contract v1 completion object from GET /custom-fields/me. */
export interface ProfileCompletion {
  requestedCount: number;
  completedCount: number;
  isComplete: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Contract wire format for birth_month_year: MM/YYYY. */
const MONTH_YEAR_RE = /^(0[1-9]|1[0-2])\/((19|20)\d{2})$/;

/** A value counts as blank when null/undefined or a whitespace-only string. */
export const isBlankValue = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/**
 * The seeded birth field is typed `text` on the wire; the UI gives it a
 * month+year picker. Detected by its seeded fieldKey.
 */
export const isMonthYearField = (field: Pick<ProfileFieldWithValue, 'fieldKey'>): boolean =>
  field.fieldKey === 'birth_month_year';

/** Parse an MM/YYYY wire value into picker parts, or null when malformed. */
export const monthYearParts = (value: unknown): { month: number; year: number } | null => {
  const match = MONTH_YEAR_RE.exec(String(value ?? ''));
  return match ? { month: Number(match[1]), year: Number(match[2]) } : null;
};

/** Build the MM/YYYY wire value from picker parts. */
export const buildMonthYear = (month: number, year: number): string =>
  `${String(month).padStart(2, '0')}/${year}`;

/** Display formatting for a field's current value; blanks render "Not provided". */
export const formatFieldValue = (field: ProfileFieldWithValue): string => {
  if (isBlankValue(field.value)) {
    return 'Not provided';
  }
  if (isMonthYearField(field)) {
    const parts = monthYearParts(field.value);
    return parts ? `${MONTH_NAMES[parts.month - 1]} ${parts.year}` : String(field.value);
  }
  switch (field.fieldType) {
    case 'boolean':
      return field.value ? 'Yes' : 'No';
    case 'dropdown': {
      const option = field.fieldOptions?.options?.find((o) => o.value === field.value);
      return option ? option.label : String(field.value);
    }
    case 'date':
      // Shared date convention (UTC calendar day, named month) - never raw ISO.
      return formatCustomFieldValue(field.value, 'date');
    default:
      return String(field.value);
  }
};

/** The value an <input type="date"> accepts: YYYY-MM-DD, or '' when blank/unparseable. */
export const toDateInputValue = (value: unknown): string => {
  if (isBlankValue(value)) {
    return '';
  }
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value));
  return match ? match[1] : '';
};

/** Per-type validation; returns a user-facing message or null when valid. */
export const validateProfileFieldValue = (field: ProfileFieldWithValue): string | null => {
  const { value } = field;
  if (isBlankValue(value)) {
    return field.isRequired ? `${field.fieldLabel} is required` : null;
  }
  if (isMonthYearField(field)) {
    return monthYearParts(value) ? null : `${field.fieldLabel} must be a valid month and year`;
  }
  const rules = field.validationRules ?? {};

  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'phone': {
      const str = String(value);
      if (rules.minLength !== undefined && str.length < rules.minLength) {
        return `${field.fieldLabel} must be at least ${rules.minLength} characters`;
      }
      if (rules.maxLength !== undefined && str.length > rules.maxLength) {
        return `${field.fieldLabel} must be no more than ${rules.maxLength} characters`;
      }
      if (rules.pattern && !new RegExp(rules.pattern).test(str)) {
        return `${field.fieldLabel} has invalid format`;
      }
      if (field.fieldType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) {
        return `${field.fieldLabel} must be a valid email`;
      }
      if (field.fieldType === 'phone' && !/^[\d\s\-+()]+$/.test(str)) {
        return `${field.fieldLabel} must be a valid phone number`;
      }
      return null;
    }
    case 'number': {
      const num = Number(value);
      if (Number.isNaN(num)) {
        return `${field.fieldLabel} must be a number`;
      }
      if (rules.min !== undefined && num < rules.min) {
        return `${field.fieldLabel} must be at least ${rules.min}`;
      }
      if (rules.max !== undefined && num > rules.max) {
        return `${field.fieldLabel} must be no more than ${rules.max}`;
      }
      return null;
    }
    case 'date': {
      const date = new Date(String(value));
      if (Number.isNaN(date.getTime())) {
        return `${field.fieldLabel} must be a valid date`;
      }
      if (rules.dateMin && date < new Date(rules.dateMin)) {
        return `${field.fieldLabel} must be after ${rules.dateMin}`;
      }
      if (rules.dateMax && date > new Date(rules.dateMax)) {
        return `${field.fieldLabel} must be before ${rules.dateMax}`;
      }
      return null;
    }
    case 'dropdown': {
      const options = field.fieldOptions?.options;
      if (options && !field.fieldOptions?.allowOther) {
        return options.some((o) => o.value === value)
          ? null
          : `${field.fieldLabel} must be one of the provided options`;
      }
      return null;
    }
    default:
      return null;
  }
};

/** Requested-by-the-org fields the learner has not filled in yet. */
export const incompleteRequestedFields = (
  fields: ReadonlyArray<ProfileFieldWithValue>
): ProfileFieldWithValue[] =>
  fields.filter((f) => f.isRequested && isBlankValue(f.value));

/**
 * "Complete your profile" prompt decision.
 * Dismissal persistence (documented choice): a localStorage marker
 * 'permanent' — written by the prompt's "Don't show again" action — hides the
 * prompt for good on that browser for that user+org. A plain close (X) writes
 * only a sessionStorage marker, so the prompt reappears on the next login/
 * session until the requested fields are complete. Any other/legacy marker
 * value is ignored (prompt shows).
 */
export const shouldShowProfilePrompt = (
  completion: Pick<ProfileCompletion, 'isComplete'> | null,
  dismissalMarker: string | null,
  sessionDismissed: boolean
): boolean =>
  completion !== null &&
  !completion.isComplete &&
  dismissalMarker !== 'permanent' &&
  !sessionDismissed;

/** Storage key for prompt dismissal markers, scoped user+org. */
export const promptDismissalKey = (userId: string, orgId: string): string =>
  `kan468.profilePrompt.dismissed.${userId}.${orgId}`;
